<template>
  <div class="flex flex-col items-start w-full">
    <label
      v-if="label"
      :for="id ? id : `${label}-field`"
      v-text="label"
      :class="labelSize ? `input--label--${labelSize}` : 'input--label'"
    />
    <input
      v-if="readonly"
      :type="type"
      :placeholder="placeholder"
      :id="id || `${label}-field`"
      spellcheck="false"
      :value="value"
      readonly
      :class="inputTextSize ? `input--text--${inputTextSize}` : 'input--text'"
    />
    <div
      v-else
      ref="autocompleteHost"
      class="places-autocomplete-host w-full"
      :class="{ 'places-autocomplete-host--small': inputTextSize === 'small' }"
      :id="id || `${label}-field`"
    />
    <ul class="input--errors">
      <li
        v-for="(error, index) in errors"
        :key="index"
        v-text="error.message"
        class="input--error"
      />
    </ul>
  </div>
</template>

<script>
import { ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { mountAddressAutocomplete } from '@/utils/googleMaps'

export default {
  name: 'PlacesStreetAddressInput',
  props: {
    label: String,
    type: { type: String, default: 'text' },
    placeholder: { type: String, default: 'Start typing your address...' },
    value: { type: [String, Number], required: true },
    errors: { type: Array, required: true },
    id: String,
    readonly: { type: Boolean, default: false },
    labelSize: String,
    inputTextSize: String,
  },
  emits: ['update:value', 'input', 'blur', 'focus', 'place-selected', 'invalid-selection'],
  setup(props, { emit }) {
    const autocompleteHost = ref(null)
    let autocompleteHandle = null

    const initAutocomplete = async () => {
      autocompleteHandle?.destroy()
      autocompleteHandle = null

      if (props.readonly || !autocompleteHost.value) {
        return
      }

      autocompleteHandle = await mountAddressAutocomplete(autocompleteHost.value, {
        placeholder: props.placeholder,
        inputClass: props.inputTextSize ? `input--text--${props.inputTextSize}` : 'input--text',
        initialValue: props.value ? String(props.value) : '',
        onPlaceSelected: (parsed) => {
          emit('update:value', parsed.street)
          emit('place-selected', parsed)
        },
        onInvalidSelection: () => emit('invalid-selection'),
        onValueChange: (value) => emit('update:value', value),
        onInput: () => emit('input'),
        onBlur: () => emit('blur'),
        onFocus: () => emit('focus'),
      })

      if (props.value) {
        autocompleteHandle.setValue(String(props.value))
      }
    }

    onMounted(() => {
      nextTick(() => initAutocomplete())
    })

    onBeforeUnmount(() => {
      autocompleteHandle?.destroy()
    })

    watch(
      () => props.readonly,
      () => {
        nextTick(() => initAutocomplete())
      },
    )

    watch(
      () => props.value,
      (newValue) => {
        autocompleteHandle?.setValue(newValue ? String(newValue) : '')
      },
    )

    return { autocompleteHost }
  },
}
</script>
